import {
  BasePropertyDTO,
  BooleanPropertyDTO,
  ColorPropertyDTO,
  DBListPropertyDTO,
  DoublePropertyDTO,
  FloatPropertyDTO,
  IntPropertyDTO,
  ListEnumDTO,
  ListPropertyDTO,
  LongPropertyDTO,
  PreferenesDTO,
  StringPropertyDTO,
  TimePropertyDTO,
} from 'business/models/Models';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RMInput } from '../ProteinFormulation/RMInput';
import { CategoryCard } from './CategoryCard';
import './globalPrefrences.css';
import { headerButtonsColor, isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import { ReactComponent as AdvancedIcon } from '../../../Icons/GlobalPreferences/Advanced.svg';
import { ReactComponent as DispenseIcon } from '../../../Icons/GlobalPreferences/Dispense.svg';
import { ReactComponent as ImagingIcon } from '../../../Icons/GlobalPreferences/Imaging.svg';
import { ReactComponent as MatrixIcon } from '../../../Icons/GlobalPreferences/Matrix.svg';
import { ReactComponent as OptimizationIcon } from '../../../Icons/GlobalPreferences/Optimization.svg';
import cloneDeep from 'lodash/cloneDeep';
import SwitchButton from 'components/customComponents/Controls/SwitchButton';
import '../../../App.css';
import { InputWithIcon } from 'components/input';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { RMColorPicker } from './GlobalPrefrences';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { useBackdropLoading, useModalHandlers } from 'components/modal';
import { Preference } from './constants';
import PlateTypeService from 'business/services/PlateTypeService';
import { IOption } from 'components/input/selectInput';
import { KeyboardKeys, VerticalArrowKeys } from 'constants/';
import { useEventListener } from 'CustomHooks/useEventListener';
import {
  validateProperty,
  timePropertyToPickerValue,
  pickerValueToTimeProperty,
} from './utils';
import {
  FmlxButton,
  FmlxDatePicker,
  FmlxDivider,
  FmlxDropdown,
  FmlxModal,
} from 'fmlx-common-ui';
import './styles.css';
import { ResetWarningModalBody } from './resetWarningModalBody';
import { PropertyIconMaps } from './PropertyIcons';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { systemPropertiesCache } from 'generic/systemPropertiesCache';
import './Preferences.scss';
import { ContainerPanel } from './ContainerPanel';
import { PropsViewPanel } from './PropsViewPanel';

type CategoryProperty = {
  categoryName: string;
  props: BasePropertyDTO[];
  selected?: boolean;
  icon?: any;
  iconName?: any;
  iconSubFolder?: any;
};
let beforeSearchSelectedCategory = -1;
let updatedProperties: BasePropertyDTO[] = [];
let unchangedProperties: CategoryProperty[] = [];
let searchedProperties: CategoryProperty[] = [];

interface Props {
  currentPreference: string;
  groupId?: number;
}

export const Preferences = (props: Props) => {
  const { currentPreference, groupId } = props;
  const [categories, setCategories] = useState<CategoryProperty[]>([]);
  const [propsView, setPropsView] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [originalPlateTypes, setOriginalPlateTypes] = useState([]);
  const [selectedPlateId, setSelectedPlateId] = useState<number>(0);
  const [showContainers, setShowContainers] = useState<boolean>(false);
  const [isPlatePropertiesSearched, setIsPlatePropertiesSearched] =
    useState<boolean>(false);
  const { showBackdrop, hideBackdrop } = useBackdropLoading();
  const { showModal, hideModal } = useModalHandlers();
  const preferencesBroadcastChannel = useBroadcast(CHANNEL_TYPES.PREFERENCES);

  const PLATE_CATEGORY_NAME = 'Plate';

  const handlePlateTypeSelect = (e: any) => {
    setSelectedPlateId(e.target.value);
  };

  useEffect(() => {
    updatePropertiesView();
  }, [selectedPlateId, isPlatePropertiesSearched, searchText, categories]);

  const containers = useMemo(() => {
    if (originalPlateTypes == null) {
      return [];
    }
    const arr: IOption[] = originalPlateTypes?.map((robot) => {
      return {
        value: robot.id,
        title: robot.name,
      };
    });
    return arr;
  }, [originalPlateTypes, selectedPlateId]);

  async function getPropNames() {
    const gpService: GlobalPreferencesService = new GlobalPreferencesService();
    const plateTypeService: PlateTypeService = new PlateTypeService();

    let allProps: BasePropertyDTO[];

    switch (currentPreference) {
      case Preference.UserPreference:
        allProps = await gpService.getAllPropNames(Preference.UserPreference);
        break;
      case Preference.GroupPreference:
        allProps = await gpService.getAllPropNames(Preference.GroupPreference, groupId);
        break;
      default:
        break;
    }
    const tempCategories: CategoryProperty[] = [];
    allProps.forEach((prop) => {
      if (
        prop.categoryName != '' &&
        tempCategories.findIndex((cat) => cat.categoryName == prop.categoryName) < 0
      ) {
        tempCategories.push({ categoryName: prop.categoryName, props: [prop] });
      } else {
        tempCategories.find((c) => c.categoryName == prop.categoryName).props.push(prop);
      }
    });
    const displayCategories = categoriesDisplayNameConverter(tempCategories);
    updatePropertiesView();
    setCategories([...displayCategories]);
    unchangedProperties = cloneDeep([...displayCategories]);
    hideBackdrop();

    const plateTypes = await plateTypeService.getAllPlateTypes();
    setSelectedPlateId(plateTypes.saveContainerDTOs[0].id);
    setOriginalPlateTypes(plateTypes.saveContainerDTOs);
  }

  useEffect(() => {
    getPropNames();
    showBackdrop('Loading...');
  }, []);

  function onChangeProp(prop: BasePropertyDTO, e: any) {
    if (prop instanceof ListEnumDTO) {
      const val = prop.options.find((opt) => opt.id === parseInt(e));
      prop.value = val;
    } else if (prop instanceof DBListPropertyDTO || prop instanceof ListPropertyDTO) {
      prop.value = prop.options.find((opt) => opt.name === e.target.value);
    } else if (prop instanceof ColorPropertyDTO) {
      prop.value = e;
    } else if (prop instanceof IntPropertyDTO || prop instanceof LongPropertyDTO) {
      prop.value = parseInt(e.target.value);
    } else if (prop instanceof DoublePropertyDTO || prop instanceof FloatPropertyDTO) {
      prop.value = e.target.value;
    } else if (prop instanceof TimePropertyDTO) {
      prop.value = e;
    } else if (prop instanceof StringPropertyDTO) {
      prop.value = e.target.value;
    } else if (prop instanceof BooleanPropertyDTO) {
      prop.value = e;
    }

    if (prop.key) {
      const idx = updatedProperties.findIndex(
        (p) => p.name === prop.name && p.key === prop.key,
      );
      if (idx >= 0) {
        updatedProperties[idx] = prop;
      } else {
        updatedProperties.push(prop);
      }
    } else {
      const idx = updatedProperties.findIndex((p) => p.name === prop.name);
      if (idx >= 0) {
        updatedProperties[idx] = prop;
      } else {
        updatedProperties.push(prop);
      }
    }

    setSaveDisabled(false);
    setCategories([...categories]);
    updatePropertiesView();
  }

  function updatePropertiesView() {
    let propsView;
    if (searchedProperties.length <= 0) {
      const selectedCategory = categories.find((c) => c.selected === true);
      if (!isNullOrUndefined(selectedCategory)) {
        if (selectedCategory.categoryName === PLATE_CATEGORY_NAME) {
          if (isPlatePropertiesSearched) {
            setPropsView(propsView);
            return;
          }
          const props = selectedCategory.props.filter(
            (p) => p.key.foreignID == selectedPlateId,
          );
          propsView = renderProperties(props);
          setShowContainers(true);
        } else {
          setShowContainers(false);
          propsView = renderProperties(selectedCategory.props);
        }
      }
    } else {
      propsView = [];

      const selectedIndex = categories.findIndex((c) => c.selected === true);
      if (categories[selectedIndex]?.categoryName === PLATE_CATEGORY_NAME) {
        updateSearchPropertiesAccordingToContainer(selectedIndex, searchText);
      }

      searchedProperties.forEach((cat) => {
        const props = renderProperties(cat.props);
        const catName = <div className="text-h3 text-black"> {cat.categoryName} </div>;
        propsView = [...propsView, catName, ...props];
      });
    }
    setPropsView(propsView);
  }

  const updateSearchPropertiesAccordingToContainer = (
    selectedIndex: number,
    searchTerm = '',
  ) => {
    const filteredProperties = categories[selectedIndex].props.filter(
      (p) => p.key.foreignID == selectedPlateId,
    );
    searchedProperties = [];
    for (let i = 0; i < filteredProperties.length; i++) {
      if (
        filteredProperties[i].displayName
          .toLocaleLowerCase()
          .indexOf(searchTerm.toLowerCase()) >= 0
      ) {
        const index = searchedProperties.findIndex(
          (cat) => cat.categoryName == categories[selectedIndex].categoryName,
        );
        if (index < 0) {
          searchedProperties.push({
            categoryName: categories[selectedIndex].categoryName,
            props: [filteredProperties[i]],
          });
        } else {
          searchedProperties
            .find((c) => c.categoryName == categories[selectedIndex].categoryName)
            .props.push(filteredProperties[i]);
        }
      }
    }
  };

  function renderProperties(props) {
    const tempPropsView = [];
    let inputTag;
    props.forEach((prop) => {
      if (prop instanceof DBListPropertyDTO || prop instanceof ListPropertyDTO) {
        inputTag = (
          <RMSelect
            key={prop.name}
            className="PreferencesInput"
            options={prop.options.map((opt) => {
              return { id: parseInt(opt.name), name: opt.displayName };
            })}
            selectedValue={parseInt(prop.value.name)}
            onChange={(e) => onChangeProp(prop, e)}
            placeholder={'any'}
            disabled={false}
          />
        );
      } else if (prop instanceof ListEnumDTO) {
        inputTag = (
          <div className="w-[35%]">
            <FmlxDropdown
              id={'user-preferences-dropdown'}
              key={prop.name}
              items={prop.options
                .slice()
                .sort((a, b) => a.id - b.id)
                .map((opt) => {
                  const iconMap = PropertyIconMaps[prop.name];
                  const icon = iconMap?.[opt.displayName];
                  return {
                    id: opt.id,
                    text: icon
                      ? ((
                          <div className="flex gap-x-2">
                            {icon}
                            {opt.displayName}
                          </div>
                        ) as any)
                      : opt.displayName,
                    group: undefined,
                    disabled: false,
                  };
                })}
              selectedItem={prop.value.id}
              onChange={(e) => onChangeProp(prop, e)}
              placeholder={'any'}
              disabled={false}
              menuProps={undefined}
              menuPosition={undefined}
            />
          </div>
        );
      } else if (prop instanceof TimePropertyDTO) {
        inputTag = (
          <div className="PreferencesTimeInput">
            <FmlxDatePicker
              id={`preferences-renderproperties-input-time-${prop.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
              key={prop.name}
              variant={FmlxDatePicker.Variant.TIME}
              value={timePropertyToPickerValue(prop.value)}
              onChange={(e, value) =>
                onChangeProp(prop, pickerValueToTimeProperty(value))
              }
            />
          </div>
        );
      } else if (prop instanceof StringPropertyDTO) {
        inputTag = (
          <input
            id="preferences-renderproperties-input-stringproperty"
            key={prop.name}
            className="inputFieldDatails PreferencesInput"
            type="text"
            onChange={(e) => {
              onChangeProp(prop, e);
            }}
            value={prop.value}
          />
        );
      } else if (
        prop instanceof IntPropertyDTO ||
        prop instanceof DoublePropertyDTO ||
        prop instanceof LongPropertyDTO ||
        prop instanceof FloatPropertyDTO
      ) {
        inputTag = (
          <RMInput
            key={prop.name}
            className="inputFieldDetails PreferencesInput"
            onChange={(e) => {
              onChangeProp(prop, e);
            }}
            value={prop.value}
            allowDecimals={
              prop instanceof DoublePropertyDTO || prop instanceof FloatPropertyDTO
            }
          />
        );
      } else if (prop instanceof BooleanPropertyDTO) {
        inputTag = (
          <SwitchButton
            key={prop.name}
            onToggle={(e) => {
              onChangeProp(prop, e);
            }}
            isOn={prop.value}
          />
        );
      } else if (prop instanceof ColorPropertyDTO) {
        inputTag = (
          <RMColorPicker
            key={prop.name}
            color={prop.value}
            onChange={(e) => onChangeProp(prop, e)}
            showChangeButton={true}
          />
        );
      }
      tempPropsView.push(
        <div
          id="preferences-renderproperties-wrapper"
          key={prop.name + '_div'}
          className="PreferencesPropertyRow"
        >
          <div
            id="preferences-renderproperties-labelgroup"
            className="flex flex-col PreferencesPropertyLabelGroup"
          >
            <label
              id="preferences-renderproperties-label"
              key={prop.name + '_label'}
              className="text-label flex text-black mb-0"
            >
              {prop.displayName}
            </label>
            <label
              id="preferences-renderproperties-description"
              key={prop.name + '_label'}
              className="text-label flex mb-0 PreferencesPropertyDescription"
            >
              Specifies the {prop.displayName}.
            </label>
          </div>
          {inputTag}
        </div>,
      );
    });
    return tempPropsView;
  }

  function categoriesDisplayNameConverter(categories: CategoryProperty[]) {
    for (let i = 0; i < categories.length; i++) {
      switch (categories[i].categoryName.toLowerCase()) {
        case 'project':
          categories[i].categoryName = 'Project';
          categories[i].selected = true;
          beforeSearchSelectedCategory = i;
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-projectpref-icon"
              name="ProjectPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={categories[i].selected ? '#006AF5' : '#737373'}
            />
          );
          categories[i].iconName = 'ProjectPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'experiment':
          categories[i].categoryName = 'Experiment';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-experimentpref-icon"
              name="ExperimentPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={categories[i].selected ? '#006AF5' : '#737373'}
            />
          );
          categories[i].iconName = 'ExperimentPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'canvas':
          categories[i].categoryName = 'Canvas';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-canvaspref-icon"
              name="CanvasPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={'#006AF5'}
            />
          );
          categories[i].iconName = 'CanvasPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'drop':
          categories[i].categoryName = 'Drop';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-droppref-icon"
              name="DropPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={'#006AF5'}
            />
          );
          categories[i].iconName = 'DropPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'scoring':
          categories[i].categoryName = 'Scoring';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-scoringpref-icon"
              name="ScoringPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={'#006AF5'}
            />
          );
          categories[i].iconName = 'ScoringPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'explorertree':
          categories[i].categoryName = 'Explore';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-treeexplorerpref-icon"
              name="TreeExplorerPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={'#006AF5'}
            />
          );
          categories[i].iconName = 'TreeExplorerPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'labelprinting':
          categories[i].categoryName = 'Label Printing';
          categories[i].iconName = 'Print';
          break;
        case 'matrixmaker':
          categories[i].categoryName = 'Matrix Maker';
          categories[i].icon = (
            <MatrixIcon id="preferences-categoriesdisplaynameconverter-matrixicon" />
          );
          categories[i].iconName = 'ProjectPerf';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'imaging':
          categories[i].categoryName = 'Imaging';
          categories[i].icon = (
            <ImagingIcon id="preferences-categoriesdisplaynameconverter-imagingicon" />
          );
          categories[i].iconName = 'ProjectPerf';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'optimization':
          categories[i].categoryName = 'Optimization';
          categories[i].icon = (
            <OptimizationIcon id="preferences-categoriesdisplaynameconverter-optimizationicon" />
          );
          categories[i].iconName = 'ProjectPerf';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'dispensingrobots':
          categories[i].categoryName = 'Dispensing Robots';
          categories[i].icon = (
            <DispenseIcon id="preferences-categoriesdisplaynameconverter-dispenseicon" />
          );
          categories[i].iconName = 'ProjectPerf';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'misc':
          categories[i].categoryName = 'Miscellaneous';
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-miscpref-icon"
              name="MiscPref"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={'#006AF5'}
            />
          );
          categories[i].iconName = 'MiscPref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'advanced':
          categories[i].categoryName = 'Advanced';
          categories[i].icon = (
            <AdvancedIcon id="preferences-categoriesdisplaynameconverter-advancedicon" />
          );
          categories[i].iconName = 'ProjectPerf';
          categories[i].iconSubFolder = 'Preferences';
          break;
        case 'plate':
          categories[i].categoryName = PLATE_CATEGORY_NAME;
          categories[i].icon = (
            <RMSvgIcon
              id="preferences-categoriesdisplaynameconverter-svg-plate-icon"
              name="Plate"
              subfolder="Preferences"
              width="24px"
              height="24px"
              fill={categories[i].selected ? '#006AF5' : '#737373'}
            />
          );
          categories[i].iconName = 'PlatePref';
          categories[i].iconSubFolder = 'Preferences';
          break;
        default:
          break;
      }
    }
    return categories;
  }

  function setSelectedCategory(categoryName: string) {
    categories.map((c) => (c.selected = false));
    categories.find((c) => c.categoryName == categoryName).selected = true;
    searchedProperties = [];
    setSearchText('');
    updatePropertiesView();
    setCategories([...categories]);
    setIsPlatePropertiesSearched(false);
  }

  function searchChangeHandler(e) {
    setSearchText(e.target.value);
    const selectedIndex = categories.findIndex((c) => c.selected === true);
    if (selectedIndex >= 0) {
      beforeSearchSelectedCategory = selectedIndex;
      if (categories[selectedIndex].categoryName !== PLATE_CATEGORY_NAME) {
        categories[selectedIndex].selected = false;
      } else {
        setIsPlatePropertiesSearched(true);
      }
    }
    searchedProperties = [];
    if (e.target.value === '') {
      setSelectedCategory(categories[beforeSearchSelectedCategory].categoryName);
      setIsPlatePropertiesSearched(false);
    } else {
      const keyword = e.target.value;

      if (categories[selectedIndex]?.categoryName === PLATE_CATEGORY_NAME) {
        updateSearchPropertiesAccordingToContainer(selectedIndex, keyword);
        updatePropertiesView();
        return;
      }

      for (let i = 0; i < categories.length; i++) {
        for (let j = 0; j < categories[i].props.length; j++) {
          if (categories[i].categoryName !== PLATE_CATEGORY_NAME) {
            if (
              categories[i].props[j].displayName
                .toLocaleLowerCase()
                .indexOf(keyword.toLowerCase()) >= 0
            ) {
              const index = searchedProperties.findIndex(
                (cat) => cat.categoryName == categories[i].categoryName,
              );
              if (index < 0) {
                searchedProperties.push({
                  categoryName: categories[i].categoryName,
                  props: [categories[i].props[j]],
                });
              } else {
                searchedProperties
                  .find((c) => c.categoryName == categories[i].categoryName)
                  .props.push(categories[i].props[j]);
              }
            }
          }
        }
      }
      updatePropertiesView();
    }
  }

  async function saveHandlder() {
    const invalidProps: string[] = [];
    const allProperties = categories.flatMap((c) => c.props);

    for (const prop of updatedProperties) {
      const error = validateProperty(prop, allProperties);
      if (error) {
        invalidProps.push(`${prop.displayName || prop.name}: ${error}`);
      }
    }

    if (invalidProps.length > 0) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Validation failed:\n${invalidProps[0]}`,
      );
      return;
    }

    showBackdrop('Saving...');
    const service = new GlobalPreferencesService();
    const preferences = new PreferenesDTO();
    preferences.baseProperties = updatedProperties;
    preferences.preferenceType = currentPreference;
    const isSuccess = await service.setAllProperties(preferences, groupId);
    hideBackdrop();
    if (isSuccess) {
      updatedProperties.forEach((prop) => systemPropertiesCache.clear(prop.name));
      preferencesBroadcastChannel?.postMessage({
        action: CHANNEL_ACTIONS.PROPERTY_UPDATE,
        payload: { propertyNames: updatedProperties.map((prop) => prop.name) },
      });
      setSaveDisabled(true);
      updatedProperties = [];
      unchangedProperties = cloneDeep([...categories]);
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'Changes have been saved successfully.',
      );
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Changes can not be saved.',
      );
    }
  }

  function discardHandler() {
    const selectedCategoryName = categories.find((c) => c.selected === true).categoryName;
    const categoriesProps = cloneDeep([...unchangedProperties]);
    categoriesProps.map((c) => {
      c.selected = c.categoryName === selectedCategoryName;
    });

    setCategories([...categoriesProps]);
    updatedProperties = [];
    setSaveDisabled(true);
    updatePropertiesView();
  }

  const changeSelectedCategory = (direction: 'next' | 'previous') => {
    const currentCategoryIndex = categories.findIndex((cat) => cat.selected);
    const newIndex =
      direction === 'next'
        ? (currentCategoryIndex + 1) % categories.length
        : (currentCategoryIndex - 1 + categories.length) % categories.length;

    const item = document.getElementById(
      `userPreference-${categories[newIndex].categoryName}`,
    );
    setSelectedCategory(categories[newIndex].categoryName);
    if (item) {
      item.scrollIntoView({
        block: 'nearest',
      });
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (VerticalArrowKeys.includes(e.key as KeyboardKeys)) {
        changeSelectedCategory(e.key === KeyboardKeys.ArrowDown ? 'next' : 'previous');
      }
    },
    [categories],
  );

  useEventListener('keydown', handleKeyDown);

  const showResetWarningModal = () => {
    showModal({
      body: (
        <ResetWarningModalBody
          showNote={currentPreference === Preference.UserPreference}
        />
      ),
      heading: 'Warning',
      onHide: hideModal,
      size: FmlxModal.Size.MEDIUM,
      id: 'preference-warning-modal',
      customFooter: (
        <div className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-4 border-t border-t-[#CACACA] border-solid border-0">
          <FmlxButton
            type="primary"
            variant="outline"
            onClick={hideModal}
            label="Cancel"
          />
          <FmlxButton label="Confirm" onClick={resetPreferences} />
        </div>
      ),
    });
  };

  const resetPreferences = async () => {
    hideModal();
    showBackdrop('Resetting Preferences...');
    try {
      const gpService: GlobalPreferencesService = new GlobalPreferencesService();
      const success = await gpService.resetPreferences(currentPreference, groupId);
      if (success) {
        await getPropNames();
        setSaveDisabled(true);
        updatedProperties = [];
        unchangedProperties = cloneDeep([...categories]);
      }
    } catch (error) {
      console.error(error);
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Could not reset preferences.',
      );
    }
  };

  return (
    <div
      id="preferences-root"
      className="flex flex-col w-full h-[60vh] min-h-full max-h-full Preferences"
    >
      <div id="preferences-divider" className="shrink-0 pb-3 -pt-1 PreferencesDivider">
        <FmlxDivider orientation={FmlxDivider.Orientation.HORIZONTAL} flexItem />
      </div>
      <div
        id="preferences-main-container"
        className="flex flex-col flex-1 min-h-0 overflow-hidden PreferencesMainContainer"
      >
        <div className="shrink-0">
          <InputWithIcon
            icon={
              <RMSvgIcon
                id="preferences-inputwithicon-search-svg"
                name="Search"
                height={'16px'}
                width={'16px'}
                fill={headerButtonsColor}
              />
            }
            onChange={searchChangeHandler}
            value={searchText}
            containerStyle={{ marginLeft: '14px', width: '440px' }}
            inputStyle={{ height: '28px' }}
            placeholder="Type Here..."
          />
        </div>

        <div
          id="preferences-content-wrapper"
          className="flex flex-1 min-h-0 ml-2 mt-2 mb-2 mr-4 overflow-hidden"
        >
          <div
            id="preferences-category-list"
            className=" mr-2 mb-2 mt-2 PreferencesCategoryList"
          >
            {categories.map((cat) => {
              return (
                <CategoryCard
                  id={`userPreference-${cat.categoryName}`}
                  key={cat.categoryName}
                  icon={cat.icon}
                  text={cat.categoryName}
                  onClick={setSelectedCategory}
                  selected={cat.selected}
                  preferenceType={'user'}
                  iconName={cat.iconName}
                  iconSubFolder={cat.iconSubFolder}
                />
              );
            })}
          </div>
          {showContainers ? (
            <ContainerPanel
              containers={containers}
              selectedPlateId={selectedPlateId}
              onPlateTypeSelect={handlePlateTypeSelect}
            >
              {propsView}
            </ContainerPanel>
          ) : (
            <PropsViewPanel>{propsView}</PropsViewPanel>
          )}
        </div>
      </div>
      <div
        id="preferences-action-buttons"
        className="flex flex-row justify-end shrink-0 PreferencesActionButtons"
      >
        <div className="mr-auto">
          <FmlxButton
            className="mr-auto"
            label="RESET PREFERENCES"
            onClick={showResetWarningModal}
            type="basic"
          />
        </div>
        <RMCustomButton
          title={'DISCARD'}
          isDisabled={saveDisabled}
          showChevronIcon={false}
          showBorder={false}
          customStyle={{
            background: '#FF5252',
            color: 'white',
            width: '95px',
            height: '34px',
            marginRight: '10px',
          }}
          onClick={discardHandler}
        />

        <RMCustomButton
          title={'SAVE'}
          isDisabled={saveDisabled}
          showChevronIcon={false}
          showBorder={false}
          customStyle={{
            background: '#4CAF50',
            color: 'white',
            width: '70px',
            height: '34px',
          }}
          onClick={saveHandlder}
        />
      </div>
    </div>
  );
};
